import { Sparkles } from "lucide-react";
import { brl, pct, num, numStr, parseNum } from "@/lib/format";
import { cmvTone } from "@/lib/recipeCalc";
import { Button, Card, Field, Input } from "@/components/kit";

function Kpi({ label, value, testid, tone }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }} data-testid={testid}>
      <div className="caption">{label}</div>
      <div className="h3 tnum mt-1" style={tone ? { color: tone } : undefined}>{value}</div>
    </div>
  );
}

const TONE_COLOR = { success: "var(--success)", warning: "var(--accent)", danger: "var(--danger)", muted: "var(--muted)" };

function PriceSuggestion({ totals, target, price, setPrice }) {
  if (!totals.suggested) return null;
  const same = Math.abs(parseNum(price) - totals.suggested) < 0.01;
  return (
    <div className="rounded-xl p-4 flex flex-wrap items-center gap-3" style={{ background: "var(--brand-tint)" }} data-testid="recipe-price-suggestion">
      <Sparkles size={18} style={{ color: "var(--brand)" }} />
      <div className="flex-1 min-w-[160px]">
        <div className="caption">Preço sugerido para CMV de {pct(target, 1)}</div>
        <div className="h2 tnum" data-testid="recipe-suggested-price">{brl(totals.suggested)}</div>
      </div>
      {!same && <Button size="sm" variant="secondary" onClick={() => setPrice(numStr(Math.round(totals.suggested * 100) / 100))} data-testid="apply-suggested-price-button">Usar este preço</Button>}
    </div>
  );
}

function CmvStatus({ tone, target }) {
  const bg = tone === "success" ? "rgba(57,108,75,0.12)" : tone === "warning" ? "var(--accent-soft)" : "rgba(184,58,58,0.12)";
  const text = tone === "success" ? `Dentro da meta de ${pct(target, 1)}` : tone === "warning" ? `Até 5 p.p. acima da meta de ${pct(target, 1)}` : `CMV alto · meta de ${pct(target, 1)}`;
  return <div className="rounded-xl p-3 text-sm font-semibold" style={{ background: bg, color: TONE_COLOR[tone] }} data-testid="recipe-cmv-status">{text}</div>;
}

export default function FinancialSummary({ totals, portions, price, setPrice, target, unitTarget, ownTarget, setOwnTarget, yieldInfo }) {
  const tone = cmvTone(totals.cmvPct, target);
  return (
    <Card className="flex flex-col gap-4" data-testid="financial-summary">
      <div className="h3">Resumo financeiro · CMV teórico</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Preço de venda no cardápio (R$)">
          <Input inputMode="decimal" placeholder="0,00" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="recipe-price-input" />
        </Field>
        <Field label="Meta de CMV deste prato (%)" hint={`Vazio = meta da unidade (${pct(unitTarget, 1)})`}>
          <Input inputMode="decimal" placeholder={numStr(unitTarget)} value={ownTarget} onChange={(e) => setOwnTarget(e.target.value)} data-testid="recipe-own-target-input" />
        </Field>
      </div>
      <PriceSuggestion totals={totals} target={target} price={price} setPrice={setPrice} />
      <div className="grid grid-cols-2 gap-3">
        <Kpi label="Custo total da receita" value={brl(totals.total)} testid="recipe-total-cost" />
        <Kpi label={`Custo por porção (÷ ${portions || 1})`} value={brl(totals.perPortion)} testid="recipe-cost-portion" />
        {yieldInfo && <Kpi label={`Custo por ${yieldInfo.unit} rendido (÷ ${num(yieldInfo.qty)} ${yieldInfo.unit})`} value={brl(totals.total / yieldInfo.qty)} testid="recipe-cost-yield-unit" />}
        <Kpi label="CMV teórico (%)" value={pct(totals.cmvPct)} tone={TONE_COLOR[tone]} testid="recipe-cmv-pct" />
        <Kpi label="CMV teórico (R$)" value={brl(totals.perPortion)} testid="recipe-cmv-value" />
        <Kpi label="Margem de contribuição" value={totals.margin === null ? "—" : brl(totals.margin)} tone={totals.margin < 0 ? "var(--danger)" : undefined} testid="recipe-margin" />
        <Kpi label="Margem (%)" value={pct(totals.marginPct)} testid="recipe-margin-pct" />
      </div>
      {totals.cmvPct !== null && <CmvStatus tone={tone} target={target} />}
      <p className="caption">CMV teórico = custo por porção ÷ preço de venda × 100 · Preço sugerido = custo por porção ÷ meta de CMV</p>
    </Card>
  );
}
